import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

const userFaqs = [
  { q: 'What is Stranger69.net?', a: 'A premium-only friend hiring platform where you can connect with verified people as hire-a-friends or friends with benefits.' },
  { q: 'Why do I need to pay ₹2500?', a: 'This fee ensures a clean and secure environment. It filters out junk users and creates a space only for genuine people.' },
  { q: 'Is my ₹2500 one-time or monthly?', a: 'It is a monthly subscription—access for 30 days. Renew to continue.' },
  { q: 'Do I need KYC as a user?', a: 'No. Normal users can browse, pay, and use without KYC.' },
  { q: 'Can I contact any service provider directly?', a: 'Yes. After subscribing, you can chat on the platform and exchange numbers or emails with their consent.' },
  { q: 'Is voice/video calling available?', a: 'No. Only in-app messaging. You can exchange contacts if mutually agreed.' },
  { q: 'Can I cancel midway?', a: 'Access continues for the full 30 days. No refunds.' },
  { q: 'How do I pay?', a: 'We use Razorpay. You can pay via UPI, card, net banking, or wallet.' },
  { q: 'Is my payment secure?', a: 'Yes. We do not store card details. All payments are processed by Razorpay.' },
  { q: 'What is "Hire a friend"?', a: 'Platonic companionship—someone to talk to, hang out with, or accompany you for events or travel.' },
  { q: 'What is FWB?', a: 'Friendship with Benefits is an 18+ section for consensual adult connections. You must be 18+ to access.' },
  { q: 'Can I hide my profile?', a: 'Yes. In Settings you can choose to hide your profile from search.' },
  { q: 'How do I report or block someone?', a: 'Use the report or block option on their profile or in chat. We take safety seriously.' },
  { q: 'Do you share my data?', a: 'We do not sell your data. See our Privacy Policy for details.' },
  { q: 'What if I forget my password?', a: 'Use "Forgot password" on the login page. We will send a reset link to your email.' },
  { q: 'Can I have multiple accounts?', a: 'One account per email. Creating multiple accounts may result in suspension.' },
  { q: 'How do I get verified as a user?', a: 'User verification is optional. Service providers go through a separate verification process.' },
  { q: 'What are favourites?', a: 'Save providers you like to your Favourites list for quick access later.' },
  { q: 'Can I book a friend for a specific time?', a: 'Yes. Bookings can be made from a provider\'s profile. They can confirm or decline.' },
  { q: 'What happens when my subscription expires?', a: 'You lose access to chat and full profiles. You can renew anytime from the Payment page.' },
];

const providerFaqs = [
  { q: 'How can I become a service provider?', a: 'Register and select "Service Provider". Pay the ₹5,000 monthly fee, complete verification, and set up your profile.' },
  { q: 'What is the fee for service providers?', a: '₹5,000 one-time monthly registration fee to verify and list your profile.' },
  { q: 'Do I need to submit documents?', a: 'Yes. Verification may require ID and selfie. Data is encrypted and secure.' },
  { q: 'Can I choose both hire a friend and FWB?', a: 'Yes. You can select both categories in your profile.' },
  { q: 'Do I set my own pricing?', a: 'Yes. You can set hourly rate and other charges in your profile.' },
  { q: 'How do I get verified?', a: 'Request verification from your profile. Our team will review and update your status.' },
  { q: 'What is verification status?', a: 'Pending = under review. Verified = approved. Verified profiles get a badge and more visibility.' },
  { q: 'Can I edit my profile anytime?', a: 'Yes. Go to My Profile in the dashboard to update categories, pricing, and Do\'s and Don\'ts.' },
  { q: 'How do I receive messages?', a: 'Messages appear in My Chats. You can reply from there.' },
  { q: 'How do bookings work?', a: 'Users can request a booking from your profile. You see requests in My Bookings and can confirm or decline.' },
  { q: 'Can I hide my profile?', a: 'Yes. In Settings you can hide your profile from search when you are not available.' },
  { q: 'Do I get paid through the platform?', a: 'Payment for your time is arranged between you and the user. We do not process those payments.' },
  { q: 'What if someone reports me?', a: 'We review reports. False reports are ignored. Violations can lead to suspension.' },
  { q: 'Can I block users?', a: 'Yes. Block from chat or profile. Blocked users cannot message or see you.' },
  { q: 'How are reviews shown?', a: 'Users who have connected with you can leave a rating and comment. These appear on your profile.' },
  { q: 'Can I delete a bad review?', a: 'You cannot delete reviews. Only the reviewer can remove their own review.' },
  { q: 'Is my identity safe?', a: 'We do not share your personal details with other users. You choose what to show on your profile.' },
  { q: 'What are Do\'s and Don\'ts?', a: 'A section where you list your boundaries and preferences so users know what to expect.' },
  { q: 'Can I switch back to user only?', a: 'Your role is set at registration. Contact support if you need to change it.' },
  { q: 'When is my provider fee due?', a: 'The ₹5,000 fee is monthly. Renew before it expires to keep your profile listed.' },
];

const FAQPage = () => {
  const [open, setOpen] = useState(null);
  const [section, setSection] = useState('user');

  const faqs = section === 'user' ? userFaqs : providerFaqs;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white text-center mb-8">
        FAQs
      </h1>
      <div className="flex gap-2 mb-8">
        <button
          type="button"
          onClick={() => setSection('user')}
          className={`px-4 py-2 rounded-xl font-medium transition-colors ${
            section === 'user' ? 'bg-brand-pink text-white' : 'bg-slate-700 text-slate-300'
          }`}
        >
          For Users
        </button>
        <button
          type="button"
          onClick={() => setSection('provider')}
          className={`px-4 py-2 rounded-xl font-medium transition-colors ${
            section === 'provider' ? 'bg-brand-pink text-white' : 'bg-slate-700 text-slate-300'
          }`}
        >
          For Service Providers
        </button>
      </div>
      <div className="space-y-2">
        {faqs.map((faq, i) => (
          <div key={i} className="rounded-xl border border-slate-600 overflow-hidden bg-slate-800/60">
            <button
              type="button"
              className="w-full flex items-center justify-between p-4 text-left font-medium text-white hover:bg-slate-700/50"
              onClick={() => setOpen(open === i ? null : i)}
              aria-expanded={open === i}
            >
              {faq.q}
              <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${open === i ? 'rotate-180' : ''}`} />
            </button>
            {open === i && (
              <div className="px-4 pb-4 text-slate-400 text-sm border-t border-slate-600 pt-2">
                {faq.a}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FAQPage;
